/**
 * PremiumIconChip — a polished, 3D-style "app icon" for feature cards.
 * Multi-layer gradient (base + glossy top highlight + inner shadow depth) +
 * ring + drop-shadow.  Looks like a real iOS/premium SaaS product icon,
 * not a flat lucide chip.
 *
 * Props:
 *   Icon: a lucide-react icon component
 *   gradient: tailwind gradient stops, e.g. "from-cyan-400 to-indigo-600"
 *   size: pixel size of the chip (default 56)
 *   iconSize: pixel size of the icon (default 24)
 *   glow: hex color for the drop-shadow glow (default matches gradient)
 */
export default function PremiumIconChip({
  Icon,
  gradient = 'from-cyan-400 to-indigo-600',
  size = 56,
  iconSize = 24,
  glow = 'rgba(0,240,255,0.5)',
  className = '',
}) {
  return (
    <div
      className={`relative rounded-2xl overflow-hidden ${className}`}
      style={{
        width: size,
        height: size,
        boxShadow: `0 10px 25px -8px ${glow}, 0 2px 6px -2px rgba(0,0,0,0.4)`,
      }}
    >
      {/* Base gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />

      {/* Glossy top-left highlight */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.15) 30%, transparent 55%)',
        }}
      />

      {/* Inner bottom depth shadow */}
      <div
        className="absolute inset-0"
        style={{
          boxShadow: 'inset -3px -10px 20px rgba(0,0,0,0.25), inset 2px 2px 4px rgba(255,255,255,0.15)',
        }}
      />

      {/* Ring / rim */}
      <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/25" />

      {/* Icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <Icon
          size={iconSize}
          strokeWidth={2.2}
          className="text-white"
          style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.35))' }}
        />
      </div>

      {/* Faint sheen line across middle */}
      <div
        className="absolute inset-x-0 pointer-events-none"
        style={{
          top: '50%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
        }}
      />
    </div>
  );
}
